import CreditCardIcon from '@mui/icons-material/CreditCard';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCart } from 'services/cart/CartContext';
import {
  createBeerStorePayment,
  createOrderDocument,
} from 'services/paymentService';
import { auth } from 'services/firebase/client';
import { useSubscription } from 'AppRoutes/context/SubscriptionContext';

// import PaymentMethodTabs, {
//   type PaymentMethodTabValue,
// } from 'components/PaymentMethodTabs';
// import BeerStorePixTab from './BeerStorePixTab';
import {
  buildBeerStoreOrderPayload,
  buildOrderItems,
  getCartItemsForPayment,
  validateBeerStoreOrderPrerequisites,
  type BeerStoreOrderData,
} from './beerStoreOrderUtils';
import { roundCurrency, sumCurrency } from 'utils/currency';
import styles from './styles';
import { initMercadoPago, CardPayment } from '@mercadopago/sdk-react';

initMercadoPago('APP_USR-a95da5cf-9b11-4dba-b56d-061a1e0be09f');

const BeerStorePayment = () => {
  const navigate = useNavigate();
  const { cart, clearCart } = useCart();
  const { hasActiveSubscription, personalData } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderData, setOrderData] = useState<BeerStoreOrderData | null>(null);
  // const [paymentTab, setPaymentTab] = useState<PaymentMethodTabValue>('card');

  useEffect(() => {
    const savedOrderData = localStorage.getItem('beerStoreOrderData');

    if (savedOrderData) {
      try {
        const parsedData = JSON.parse(savedOrderData);
        setOrderData(parsedData);
      } catch (error) {
        navigate('/beer-store-shipping');
        return;
      }
    } else {
      navigate('/beer-store-shipping');
      return;
    }

    if (!cart.items.length) {
      navigate('/beer-store');
    }
  }, [navigate, cart.items.length]);

  const handleBack = () => {
    navigate('/beer-store');
  };

  const subtotal = roundCurrency(
    hasActiveSubscription ? cart.subscriberTotalValue : cart.totalValue,
  );

  const getShippingCost = () => {
    if (orderData?.shipping?.price !== undefined) {
      return roundCurrency(orderData.shipping.price);
    }

    const savedOrderData = localStorage.getItem('beerStoreOrderData');
    if (savedOrderData) {
      try {
        const parsedData = JSON.parse(savedOrderData);
        return roundCurrency(parsedData?.shipping?.price || 0);
      } catch {
        return 0;
      }
    }

    return 0;
  };

  const shippingCost = getShippingCost();
  const total = sumCurrency(subtotal, shippingCost);

  useEffect(() => {
    if (cart.items.length > 0 && total <= 0) {
      navigate('/beer-store-error');
    }
  }, [cart.items.length, total, navigate]);

  const handleCardSubmit = useCallback(
    async (param: any) => {
      let currentOrderData = orderData;
      if (!currentOrderData) {
        const savedOrderData = localStorage.getItem('beerStoreOrderData');
        if (savedOrderData) {
          try {
            currentOrderData = JSON.parse(savedOrderData);
            setOrderData(currentOrderData);
          } catch (error) {
            // Erro ao recarregar dados
          }
        }
      }

      setIsProcessing(true);

      try {
        const cartItemsForPayment = getCartItemsForPayment(
          cart.items,
          hasActiveSubscription,
        );

        const response = await createBeerStorePayment({
          token: param.token,
          payer: {
            email: param.payer.email,
            identification: {
              type: 'CPF',
              number: param.payer.identification.number,
            },
          },
          amount: total,
          cartItems: cartItemsForPayment,
          isSubscriber: hasActiveSubscription,
        });

        if (!response || response.error || response.status !== 'approved') {
          navigate('/beer-store-error');
          return;
        }

        try {
          if (!currentOrderData) {
            throw new Error('Dados do pedido não encontrados');
          }

          validateBeerStoreOrderPrerequisites(
            currentOrderData,
            personalData,
          );

          if (!auth.currentUser?.uid || !auth.currentUser.email) {
            throw new Error('Usuário não autenticado');
          }

          const orderItems = buildOrderItems(
            cart.items,
            hasActiveSubscription,
          );
          const paymentId = response.payment_id || response.id;

          const orderPayload = buildBeerStoreOrderPayload({
            userId: auth.currentUser.uid,
            userEmail: auth.currentUser.email,
            items: orderItems,
            subtotal,
            shippingCost,
            total,
            hasActiveSubscription,
            orderData: currentOrderData,
            personalData,
            payment: {
              method: 'credit_card',
              status: 'approved',
              transactionId: paymentId,
              paymentId,
              amount: total,
              installments: response.installments || 1,
              lastFourDigits: response.card?.last_four_digits,
              cardBrand: response.card?.first_six_digits
                ? response.card.first_six_digits.startsWith('4')
                  ? 'Visa'
                  : response.card.first_six_digits.startsWith('5')
                    ? 'Mastercard'
                    : 'Other'
                : undefined,
            },
            orderStatus: 'confirmed',
          });

          const cfOrder = await createOrderDocument(orderPayload);

          clearCart();
          localStorage.removeItem('beerStoreOrderData');

          navigate('/beer-store-success', {
            state: {
              orderId: cfOrder.id,
              orderNumber: cfOrder.orderNumber,
              total,
              items: cart.items,
              paymentId: response.id,
            },
          });
        } catch (orderError) {
          clearCart();
          localStorage.removeItem('beerStoreOrderData');

          navigate('/beer-store-success', {
            state: {
              orderId: response.payment_id || response.id,
              orderNumber: `TEMP-${response.payment_id || response.id}`,
              total,
              items: cart.items,
              error:
                'Pagamento aprovado, mas houve erro ao salvar o pedido. Entre em contato conosco.',
            },
          });
        }
      } catch (err) {
        navigate('/beer-store-error');
      } finally {
        setIsProcessing(false);
      }
    },
    [
      cart.items,
      total,
      navigate,
      hasActiveSubscription,
      clearCart,
      orderData,
      personalData,
      subtotal,
      shippingCost,
    ],
  );

  return (
    <Box sx={{ ...styles.container, px: { xs: 2, sm: 4 } }}>
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography
            variant="subtitle1"
            sx={{ ...styles.subtitle, textAlign: 'center', mb: 2 }}
          >
            FORMA DE PAGAMENTO
          </Typography>

          {/* PIX temporariamente desabilitado
          <PaymentMethodTabs value={paymentTab} onChange={setPaymentTab} />
          */}

          {/* {paymentTab === 'card' && ( */}
            <>
              <Card
                sx={{
                  ...styles.card,
                  mx: { xs: 1, sm: 2 },
                  px: { xs: 2, sm: 3 },
                }}
              >
                <CardContent sx={styles.paymentHeader}>
                  <CreditCardIcon sx={styles.icon} />
                  <Typography variant="body1">
                    Pagar com Cartão de Crédito
                  </Typography>
                </CardContent>
              </Card>

              <CardPayment
                initialization={{
                  amount: total,
                  payer: { email: auth.currentUser?.email || '' },
                }}
                onSubmit={handleCardSubmit}
                locale="pt-BR"
                customization={{
                  paymentMethods: {
                    maxInstallments: 10,
                    types: {
                      included: ['credit_card'],
                    },
                  },
                }}
              />
            </>
          {/* )} */}

          {/* PIX temporariamente desabilitado
          {paymentTab === 'pix' && (
            <BeerStorePixTab
              total={total}
              subtotal={subtotal}
              shippingCost={shippingCost}
              orderData={orderData}
              disabled={isProcessing}
            />
          )}
          */}
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={styles.subtitle}>
            RESUMO DO PEDIDO
          </Typography>
          <Card sx={{ ...styles.summaryCard, mx: { xs: 1, sm: 2 } }}>
            <CardContent>
              {cart.items.map(item => {
                const unitPrice = hasActiveSubscription
                  ? item.beer.subscriberPrice
                  : item.beer.price;
                return (
                  <Box key={item.id} sx={{ mb: 2 }}>
                    <Typography variant="body1" sx={styles.productText}>
                      {item.beer.name}
                      {hasActiveSubscription && (
                        <Typography
                          component="span"
                          sx={{ ml: 1, color: 'green', fontSize: '0.8em' }}
                        >
                          (Preço Assinante)
                        </Typography>
                      )}
                    </Typography>
                    <Typography variant="body2" sx={styles.quantityText}>
                      Quantidade: {item.quantity} x R${' '}
                      {unitPrice.toFixed(2).replace('.', ',')}
                      {hasActiveSubscription &&
                        item.beer.price !== item.beer.subscriberPrice && (
                          <Typography
                            component="span"
                            sx={{
                              ml: 1,
                              textDecoration: 'line-through',
                              color: 'gray',
                            }}
                          >
                            R$ {item.beer.price.toFixed(2).replace('.', ',')}
                          </Typography>
                        )}
                    </Typography>
                    <Typography variant="body2" sx={styles.itemTotalText}>
                      Subtotal: R${' '}
                      {(unitPrice * item.quantity).toFixed(2).replace('.', ',')}
                    </Typography>
                  </Box>
                );
              })}

              <Box sx={styles.summaryContainer}>
                <Typography variant="body2" sx={styles.summaryText}>
                  SUBTOTAL
                </Typography>
                <Typography variant="body2" sx={styles.summaryValue}>
                  R$ {subtotal.toFixed(2).replace('.', ',')}
                </Typography>
              </Box>

              <Box sx={styles.summaryContainer}>
                <Typography variant="body2" sx={styles.summaryText}>
                  FRETE
                </Typography>
                <Typography variant="body2" sx={styles.summaryValue}>
                  {shippingCost === 0
                    ? 'GRÁTIS'
                    : `R$ ${shippingCost.toFixed(2).replace('.', ',')}`}
                </Typography>
              </Box>

              {(orderData?.shipping ||
                (() => {
                  const savedOrderData =
                    localStorage.getItem('beerStoreOrderData');
                  try {
                    return savedOrderData
                      ? JSON.parse(savedOrderData)?.shipping
                      : null;
                  } catch {
                    return null;
                  }
                })()) && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{ fontSize: '0.75rem', color: '#666' }}
                  >
                    {orderData?.shipping?.description ||
                      (() => {
                        const savedOrderData =
                          localStorage.getItem('beerStoreOrderData');
                        try {
                          return savedOrderData
                            ? JSON.parse(savedOrderData)?.shipping?.description
                            : 'Método de envio selecionado';
                        } catch {
                          return 'Método de envio selecionado';
                        }
                      })()}
                  </Typography>
                </Box>
              )}

              <Box sx={styles.summaryContainer}>
                <Typography variant="body1" sx={styles.totalText}>
                  TOTAL
                </Typography>
                <Typography variant="h6" sx={styles.totalValue}>
                  R$ {total.toFixed(2).replace('.', ',')}
                </Typography>
              </Box>
            </CardContent>
          </Card>

          <Box sx={styles.footer}>
            <Button
              variant="contained"
              onClick={handleBack}
              sx={styles.advanceButton}
              disabled={isProcessing}
            >
              {isProcessing ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Voltar'
              )}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BeerStorePayment;
